import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Field } from '@/components/live';
import { geographyChildren, geographyOptions } from '@/api/lookups';
import { isSupabaseConfigured } from '@/lib/supabase';

export interface CascadeValue {
  stateId: string;
  districtIds: string[];
  blockIds: string[];
  villageIds: string[];
}

export const EMPTY_CASCADE: CascadeValue = { stateId: '', districtIds: [], blockIds: [], villageIds: [] };

/**
 * The deepest level the officer actually chose, and the places at it.
 *
 * Stopping early is a real answer, not an unfinished form: an area in-charge covers three
 * districts and every village in them, and saying so as three districts is both shorter and
 * truer than listing four hundred villages. So the level assigned is the last one with a
 * selection - and when only a state is chosen, the state.
 */
export function cascadeSelection(value: CascadeValue): { level: string; ids: string[] } {
  if (value.villageIds.length) return { level: 'village', ids: value.villageIds };
  if (value.blockIds.length) return { level: 'block', ids: value.blockIds };
  if (value.districtIds.length) return { level: 'district', ids: value.districtIds };
  if (value.stateId) return { level: 'state', ids: [value.stateId] };
  return { level: '', ids: [] };
}

/**
 * The database's word for a level, and the screen's. They differ at one: `block` is what the
 * geography master calls it and "sub-district" is what the owner and the officer call it, so the
 * summary should not suddenly switch vocabulary on the line under the field labelled otherwise.
 */
const LEVEL_WORD: Record<string, string> = {
  state: 'state', district: 'district', block: 'sub-district', village: 'village',
};

function toggle(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((entry) => entry !== id) : [...list, id];
}

/**
 * State, then district, then sub-district, then village - each narrowed by the one above, and
 * every level below the state a multiselect (owner, 2026-09-25: "multiselect of districts or
 * places. start from state, district, sub-district, villages in sequence").
 *
 * What it replaced was a Level dropdown beside a single flat Place dropdown, so choosing
 * "district" listed every activated district in the organisation in one alphabetical run - Ajmer,
 * Alwar, Eluru, Nadia - with nothing to say which state any of them was in, and two districts
 * meant opening the drawer twice. Sequence is the point: each list is the children of what was
 * chosen above it, so a district is never shown out of its state.
 *
 * ONLY ACTIVATED PLACES APPEAR, within the caller's picker scope. That is the same rule the flat
 * lists applied and the reason a level can legitimately come back empty: the organisation has not
 * taken that area on. The empty message says so rather than looking like a failed load.
 */
export function GeographyCascade({
  value,
  onChange,
}: {
  value: CascadeValue;
  onChange: (next: CascadeValue) => void;
}) {
  const states = useQuery({
    queryKey: ['geo-any', 'state'],
    queryFn: () => geographyOptions('state', undefined, undefined, 500),
    enabled: isSupabaseConfigured,
  });
  const districts = useQuery({
    queryKey: ['geo-children', 'district', value.stateId],
    queryFn: () => geographyChildren([value.stateId], 'district'),
    enabled: isSupabaseConfigured && Boolean(value.stateId),
  });
  const blocks = useQuery({
    queryKey: ['geo-children', 'block', [...value.districtIds].sort().join(',')],
    queryFn: () => geographyChildren(value.districtIds, 'block'),
    enabled: isSupabaseConfigured && value.districtIds.length > 0,
  });
  const villages = useQuery({
    queryKey: ['geo-children', 'village', [...value.blockIds].sort().join(',')],
    queryFn: () => geographyChildren(value.blockIds, 'village'),
    enabled: isSupabaseConfigured && value.blockIds.length > 0,
  });

  const districtName = useMemo(
    () => new Map((districts.data ?? []).map((row) => [row.id, row.label])),
    [districts.data],
  );

  const chosen = cascadeSelection(value);

  return (
    <>
      <Field hint="Only the states this organisation has activated." label="State" required>
        <select
          // Changing the state drops everything below it. Keeping a district from the old state
          // would assign somebody a place they were never shown.
          onChange={(event) => onChange({ ...EMPTY_CASCADE, stateId: event.target.value })}
          value={value.stateId}
        >
          <option value="">Select a state</option>
          {(states.data ?? []).map((row) => <option key={row.id} value={row.id}>{row.label}</option>)}
        </select>
      </Field>

      <Level
        count={value.districtIds.length}
        empty="No district in this state has been activated."
        hint="Tick as many as they cover. Leave the levels below blank to assign whole districts."
        label="District"
        loading={districts.isLoading}
        locked={!value.stateId ? 'Choose a state first.' : null}
        onToggle={(id) => onChange({ ...value, districtIds: toggle(value.districtIds, id), blockIds: [], villageIds: [] })}
        options={(districts.data ?? []).map((row) => ({ id: row.id, label: row.label }))}
        selected={value.districtIds}
      />

      <Level
        count={value.blockIds.length}
        empty="No sub-district under the districts chosen has been activated."
        group={(id) => districtName.get(id) ?? ''}
        hint="Blank assigns the whole district."
        label="Sub-district (block)"
        loading={blocks.isLoading}
        locked={!value.districtIds.length ? 'Choose at least one district first.' : null}
        onToggle={(id) => onChange({ ...value, blockIds: toggle(value.blockIds, id), villageIds: [] })}
        options={(blocks.data ?? []).map((row) => ({ id: row.id, label: row.label, parent: row.matched_parent_id }))}
        selected={value.blockIds}
      />

      <Level
        count={value.villageIds.length}
        empty="No village under the sub-districts chosen has been activated."
        hint="Blank assigns the whole sub-district."
        label="Village"
        loading={villages.isLoading}
        locked={!value.blockIds.length ? 'Choose at least one sub-district first.' : null}
        onToggle={(id) => onChange({ ...value, villageIds: toggle(value.villageIds, id) })}
        options={(villages.data ?? []).map((row) => ({ id: row.id, label: row.label }))}
        selected={value.villageIds}
      />

      <p className="state-note">
        {chosen.ids.length
          ? `${chosen.ids.length} ${LEVEL_WORD[chosen.level] ?? chosen.level}${chosen.ids.length === 1 ? '' : 's'} will be assigned.`
          : 'Nothing chosen yet. Pick a state, and go as deep as you need.'}
      </p>
    </>
  );
}

/** One level of the cascade: a row of chips, or the reason there is nothing to choose from. */
function Level({
  label, hint, options, selected, onToggle, locked, loading, empty, count, group,
}: {
  label: string;
  hint: string;
  options: Array<{ id: string; label: string; parent?: string | null }>;
  selected: string[];
  onToggle: (id: string) => void;
  /** Why this level cannot be used yet, or null when it can. */
  locked: string | null;
  loading: boolean;
  empty: string;
  count: number;
  /** Names the parent of an option, so one list can be read as several. */
  group?: (parentId: string) => string;
}) {
  const grouped = useMemo(() => {
    if (!group) return [{ heading: '', items: options }];
    const byParent = new Map<string, typeof options>();
    for (const option of options) {
      const key = option.parent ?? '';
      byParent.set(key, [...(byParent.get(key) ?? []), option]);
    }
    return [...byParent.entries()].map(([key, items]) => ({ heading: group(key), items }));
  }, [options, group]);

  return (
    // The reason a level is locked goes where the chips would be, not in the hint as well: the
    // first version printed "Choose a state first." twice under every level, which reads as a
    // stutter rather than as guidance.
    <Field hint={locked ? undefined : hint} label={count ? `${label} (${count})` : label}>
      {locked ? <p className="muted">{locked}</p>
        : loading ? <p className="muted">Loading...</p>
          : options.length === 0 ? <p className="state-note">{empty}</p>
            : (
              <div className="geo-cascade-level">
                {grouped.map(({ heading, items }) => (
                  <div key={heading || 'all'}>
                    {heading && <small className="muted">{heading}</small>}
                    <div className="chip-picker">
                      {items.map((option) => (
                        <label className={`chip-option${selected.includes(option.id) ? ' is-on' : ''}`} key={option.id}>
                          <input checked={selected.includes(option.id)} onChange={() => onToggle(option.id)} type="checkbox" />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
    </Field>
  );
}
